import { pipe } from 'ramda';
import { getDaysInMonth } from 'date-fns';
// import { configContext } from '@bymason/runtime-config';
import { ApiBase, PageableResult } from '@bymason/api-base';
import { formatIsoFromMonthDate, getEndpointTemplate, pipeQueryString } from '@bymason/utils';
import {
  GenerateUageReportOptions,
  GetByDayOptions,
  GetBySimsOptions,
  GetSimConnectivityOptions,
  SimModel,
  UpdateSimStatusOptions,
  UpdateSimStatusResponse,
  UsageModel,
} from './types';
// import { getByDayStubData, getBySimStubData } from './stub-data';

export * from './types';

class Connectivity extends ApiBase {
  private static instance: Connectivity;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns A ConnectivityByDay instance
   */
  public static getInstance(): Connectivity {
    if (!Connectivity.instance) {
      Connectivity.instance = new Connectivity();
    }

    return Connectivity.instance;
  }

  /**
   * Generates a device usage report via custom date range.
   * @param options GenerateUageReportOptions object.
   * @param clientToken token required for proper authorization.
   * @returns either a UsageModel array or void
   */
  public async generateUsageReport(options: GenerateUageReportOptions, clientToken: string) {
    const headers = new Headers();
    const url = this.getEndpointTemplate(
      pipe(
        () => `device/usage?`,
        pipeQueryString('email', options.email, true),
        pipeQueryString('from', formatIsoFromMonthDate(options.startDate)),
        pipeQueryString('to', formatIsoFromMonthDate(options.endDate))
      )()
    );

    // the API requires this (per spec)
    headers.append('accept', 'async');

    await this.callVoidEndpoint({
      clientToken,
      errorOn404: true,
      headers,
      method: 'GET',
      url,
    });
  }

  /**
   * Gets connectivity data by day for given month.
   * @param options GetByDayOptions object.
   * @param clientToken token required for proper authorization.
   * @returns either a UsageModel array or void
   */
  public async getByDay(options: GetByDayOptions, clientToken: string) {
    const { iccid } = options;
    const url = this.getEndpointTemplate(
      `data/usage${iccid ? '/' + iccid : ''}?date=${formatIsoFromMonthDate(options.date)}`
    );

    // DISABLING STUB DATA, USE THE +HANGAR ACCOUNT INSTEAD
    // if (configContext === 'local') {
    //   // client is running local in dev-mode, so return stubbed data
    //   console.log(
    //     'getByDay() is running locally, so returning stubbed data; constructed URL endpoint is ',
    //     url
    //   );
    //   return getByDayStubData(options);
    // }

    const dataResponse = await this.callJsonEndpoint<UsageModel[]>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    const daysInMonth = getDaysInMonth(options.date);

    const byDayData = dataResponse
      ? dataResponse.reduce<UsageModel[]>((tally, record, idx) => {
          // API returns too many days, so only include days in month
          if (idx < daysInMonth) {
            const usageDay: UsageModel = {
              ...record,
              ...{ day: record.day },
            };

            tally.push(usageDay);
          }

          return tally;
        }, [])
      : [];

    return byDayData;
  }

  /**
   * Gets connectivity data by SIM for given month.
   * @param options GetBySimsOptions object.
   * @param clientToken token required for proper authorization.
   * @returns either PageableResult<SimModel[]> or void
   */
  public async getBySim(options: GetBySimsOptions, clientToken: string) {
    const { after, before, limit, month, orderBy, orderByColumn, status } = options;

    const url = this.getEndpointTemplate(
      pipe(
        () => `sim?`,
        pipeQueryString('month', formatIsoFromMonthDate(month), true),
        pipeQueryString('status', status),
        pipeQueryString('orderByColumn', orderByColumn),
        pipeQueryString('orderBy', orderBy),
        pipeQueryString('limit', limit),
        pipeQueryString('before', before),
        pipeQueryString('after', after)
      )()
    );

    // DISABLING STUB DATA, USE THE +HANGAR ACCOUNT INSTEAD
    // if (configContext === 'local') {
    //   // client is running local in dev-mode, so return stubbed data
    //   console.log(
    //     'getBySim() is running locally, so returning stubbed data; constructed URL endpoint is ',
    //     url
    //   );
    //   return getBySimStubData(options);
    // }

    const dataResponse = await this.callJsonEndpoint<PageableResult<SimModel[]>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets connectivity data for each given sim iccid.
   * (This makes multiple api calls via Promise.allSettled())
   * @param options GetSimConnectivityOptions object.
   * @param clientToken token required for proper authorization.
   * @returns either a SimModel or void
   */
  public async getSimConnectivity(options: GetSimConnectivityOptions, clientToken: string) {
    const responses = await Promise.allSettled(
      options.iccidList.map(async (iccid) => {
        const urlTemplate = this.getEndpointTemplate('sim/:iccid');
        const url = this.expandUrlParams(urlTemplate, { iccid });

        const dataResponse = await this.callJsonEndpoint<SimModel>({
          clientToken,
          errorOn404: true,
          method: 'GET',
          url,
        });

        return dataResponse;
      })
    );

    return responses;
  }

  /**
   * Gets connectivity data for a single sim iccid.
   * @param options GetSimConnectivityOptions object.
   * @param clientToken token required for proper authorization.
   * @returns either a SimModel or void
   */
  public async getSingleSimConnectivity(iccid: string, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('sim/:iccid');
    const url = this.expandUrlParams(urlTemplate, { iccid });

    const dataResponse = await this.callJsonEndpoint<SimModel>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Updates SIM status to enable or disable it.
   * @param options ChangeSimStatusOptions object.
   * @param clientToken token required for proper authorization.
   * @returns void
   */
  public async updateSimStatus(options: UpdateSimStatusOptions, clientToken: string) {
    const url = getEndpointTemplate('default/sim', 'beta');
    const body = options;

    const dataResponse = await this.callJsonEndpoint<UpdateSimStatusResponse>({
      body: JSON.stringify(body),
      clientToken,
      errorOn404: true,
      method: 'PUT',
      url,
    });

    return dataResponse;
  }
}

export const connectivityClient = Connectivity.getInstance();
